import { isDefined } from "@clipboard-health/util-ts";
import type { DateRange } from "@src/appV2/lib";
import { usePaginatedWorkerOpenShifts } from "@src/appV2/OpenShifts/api/usePaginatedWorkerOpenShifts";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useWorkerGeoLocation } from "@src/appV2/Worker/useWorkerGeoLocation";
import { differenceInMonths, minutesToMilliseconds, startOfMonth } from "date-fns";
import { useState } from "react";

import { useGetShiftDiscoveryDefaultDateRange } from "../../utils/useGetShiftDiscoveryDefaultDateRange";
import { useGetOpenShiftsForDates } from "../Open/useGetOpenShiftsForDates";
import { usePaginatedOpenShiftDates } from "../Open/usePaginatedOpenShiftDates";
import { useCalendarDataProcessing } from "./useCalendarDataProcessing";
import { useCalendarFilters } from "./useCalendarFilters";
import { useCalendarLoadMore } from "./useCalendarLoadMore";

interface UseShiftDiscoveryCalendarDataProps {
  initialDateRange: DateRange;
  /**
   * When true, enables month-by-month loading mode where data is loaded for specific months on demand.
   * When false (default), uses the original continuous range loading behavior.
   */
  monthByMonthLoading?: boolean;
}

export const useShiftDiscoveryCalendarData = (props: UseShiftDiscoveryCalendarDataProps) => {
  const { initialDateRange, monthByMonthLoading = false } = props;

  const worker = useDefinedWorker();
  const workerGeoLocation = useWorkerGeoLocation();
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const [dateRange, setDateRange] = useState<DateRange>(initialDateRange);

  const { getOpenShiftsFilter, getUrgentShiftsFilter } = useCalendarFilters(timezone);

  const defaultDateRange = useGetShiftDiscoveryDefaultDateRange();

  const {
    data: workerShiftsData,
    isSuccess: workerShiftsIsSuccess,
    isLoading: workerShiftsIsLoading,
    refetch: refetchWorkerShifts,
    fetchNextPage: fetchMoreWorkerShifts,
    isFetchingNextPage: workerShiftsIsFetchingNextPage,
  } = usePaginatedWorkerOpenShifts({
    initialDateRange: {
      startDate: startOfMonth(initialDateRange.startDate),
      endDate: initialDateRange.endDate,
    },
    groupByDate: false,
    tmz: timezone ?? "",
  });

  const {
    data: paginatedOpenShiftsCountData,
    isLoading: openShiftsCountIsLoading,
    refetch: refetchOpenShiftsCount,
    fetchNextPage: fetchMoreOpenShiftsCount,
    isFetchingNextPage: openShiftsCountIsFetchingNextPage,
  } = usePaginatedOpenShiftDates(
    {
      initialDateRange,
      filter: getOpenShiftsFilter(),
    },
    {
      enabled: isDefined(timezone) && isDefined(worker?.preference),
      staleTime: minutesToMilliseconds(1),
    }
  );

  const {
    shifts: urgentShifts,
    pagination: urgentShiftsPagination,
    isLoading: urgentShiftsIsLoading,
  } = useGetOpenShiftsForDates({
    enabled: isDefined(workerGeoLocation),
    dates: [dateRange.startDate, dateRange.endDate],
    dateRange: defaultDateRange,
    filter: getUrgentShiftsFilter(),
    includeOffers: false,
  });

  const {
    workerShiftsByDate,
    unfilteredOpenShiftsCountDataWithUrgency,
    filteredOpenShiftsCountData,
  } = useCalendarDataProcessing({
    workerShiftsData,
    workerShiftsIsSuccess,
    paginatedOpenShiftsCountData,
    urgentShifts,
    timezone,
  });

  const { loadMore } = useCalendarLoadMore({
    dateRange,
    setDateRange,
    monthByMonthLoading,
    fetchMoreWorkerShifts,
    fetchMoreOpenShiftsCount,
    urgentShiftsPagination,
  });

  const refetch = () => {
    void refetchWorkerShifts();
    void refetchOpenShiftsCount();
  };

  return {
    dateRange,
    setDateRange,
    workerShiftsByDate,
    workerShiftsIsLoading,
    workerShiftsIsFetchingNextPage,
    unfilteredOpenShiftsCountData: unfilteredOpenShiftsCountDataWithUrgency,
    filteredOpenShiftsCountData,
    openShiftsCountIsLoading,
    openShiftsCountIsFetchingNextPage,
    urgentShifts,
    urgentShiftsIsLoading,
    refetch,
    loadMore,
    canLoadMore:
      isDefined(dateRange.endDate) &&
      differenceInMonths(dateRange.endDate, dateRange.startDate) < 12,
  };
};
